import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { SpaceOut, SpaceRef } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useOrg } from "../lib/data.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { bump, useAction } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { readOnly } from "../lib/standing.ts";
import { loadMe, type PageProps, reloadAndGoHome } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmButton } from "../ui/Dialog.tsx";
import { Form, Input } from "../ui/form.tsx";
import { Async, Badge, Section } from "../ui/misc.tsx";
import { buttonClass } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";
import { NewSpaceDialog } from "./settings/newSpace.tsx";

/** A space of the organization, as its row: its name (renamed in place, admins), opening it, deleting it (owners). */
function SpaceRow(p: { space: SpaceOut; it: SpaceRef }) {
  const { space, it } = p;
  const here = it.id === space.id;
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(it.name);
  // Your spaces (the switcher's), then everything on screen (this list, the sidebar's name).
  const changed = async () => {
    await loadMe();
    bump();
  };
  const [rename, saving] = useAction(async () => {
    await api.patch(spaceApi({ id: it.id }), { name });
    setRenaming(false);
    toast(t("settings.saved"));
    await changed();
  });
  const [remove, removing] = useAction(async () => {
    await api.del(spaceApi({ id: it.id }));
    await (here ? reloadAndGoHome() : changed());
  });
  if (renaming) {
    return (
      <Form class="flex flex-wrap items-center gap-2" onSubmit={rename}>
        <div class="min-w-48 flex-1">
          <Input value={name} onValue={setName} required={true} aria-label={t("settings.name")} autofocus={true} />
        </div>
        <Button size="sm" onClick={() => setRenaming(false)}>
          {t("common.cancel")}
        </Button>
        <Button size="sm" variant="primary" submit={true} busy={saving}>
          {t("common.save")}
        </Button>
      </Form>
    );
  }
  return (
    <div class="flex flex-wrap items-center justify-between gap-3">
      <span class="flex min-w-0 items-center gap-2">
        <span class="truncate font-medium text-zinc-900">{it.name}</span>
        {here ? <Badge tone="blue">{t("settings.thisSpace")}</Badge> : null}
      </span>
      <span class="flex flex-wrap items-center gap-2">
        {here ? null : (
          <Link href={spaceUrl(it, "/triage")} class={buttonClass("secondary", "sm")}>
            {t("settings.openSpace")}
          </Link>
        )}
        {atLeast(space.role, "admin") ? (
          <Button size="sm" onClick={() => setRenaming(true)}>
            {t("settings.rename")}
          </Button>
        ) : null}
        {space.role === "owner" ? (
          <ConfirmButton
            size="sm"
            variant="danger"
            label={t("common.delete")}
            title={t("settings.deleteSpaceNamed", { name: it.name })}
            message={t("settings.deleteSpaceMessage")}
            confirmLabel={t("settings.deleteSpace")}
            typeToConfirm={it.name}
            busy={removing}
            onConfirm={remove}
          />
        ) : null}
      </span>
    </div>
  );
}

/** The organization's spaces: each renamed, opened or deleted right here, and (admins) a new one. */
export function SpacesSettings({ space }: PageProps) {
  const org = useOrg(space);
  const [adding, setAdding] = useState(false);
  const admin = atLeast(space.role, "admin");
  // Why there can't be another space: read-only, or as many as the plan allows (existing ones stay either way).
  const { limits, usage } = space.standing;
  const full = readOnly(space)
    ? t("plan.readOnly")
    : limits && usage.spaces >= limits.spaces
      ? t("plan.spacesFull", { count: limits.spaces })
      : null;
  return (
    <>
      <SettingsNav space={space} tab="/spaces" />
      <Section title={t("settings.spaces")}>
        <p class="mb-3 max-w-2xl text-sm text-zinc-600">{t("settings.spacesHint")}</p>
        <Async r={org}>
          {(o) => (
            <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
              {o.spaces.map((s) => (
                <li key={s.id} class="px-4 py-3">
                  <SpaceRow space={space} it={s} />
                </li>
              ))}
            </ul>
          )}
        </Async>
        {admin ? (
          <div class="mt-4 flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={full !== null}
              title={full ?? undefined}
              onClick={() => setAdding(true)}
            >
              {t("settings.newSpace")}
            </Button>
            {full === null ? (
              <Link href={spaceUrl(space, "/settings/import?new=1")} class={buttonClass()}>
                {t("import.newSpace")}
              </Link>
            ) : (
              <p class="self-center text-sm text-zinc-600">{full}</p>
            )}
          </div>
        ) : null}
        <NewSpaceDialog space={space} open={adding} onClose={() => setAdding(false)} />
      </Section>
    </>
  );
}
